import React from 'react';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Icon, COLORS } from '@livecommerce/design-system';

import { buildRoute } from 'constants/routes.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { useNavigationFrameContext } from 'contexts/NavigationFrameContext.jsx';
import SubMenu from 'components-v3/Sidebar/SubMenu.jsx';
import {
  disabledNavigationLinkStyles,
  navigationBaseStyles,
  navigationLinkStyles,
  titleStyles,
  navigationButtonStyles,
  IconWrapper,
  ButtonWrapper,
  MenuList,
  CustomIconWrapper,
  StyledNumberIndicator,
  StyledFlair,
} from './styles.js';
import Progress from 'pages/Vod/GetStarted/Progress.jsx';
import { useGetStartedVodStatus, STEPS_KEYS } from 'pages/Vod/GetStarted/hooks/useGetStartedVodStatus.js';
import { useDelegatedFrameContext } from 'contexts/DelegatedFrameContext.jsx';

const STEPS = Object.keys(STEPS_KEYS);

const ProductsNavigationListItem = ({
  title,
  icon,
  flair,
  route,
  routeParams,
  exact = false,
  url,
  disabled,
  submenu,
  align,
  dataTestId,
  numberIndicators,
  ...props
}) => {
  const { setSelectedNavigationItemTitle, selectedNavigationItemTitle } = useNavigationFrameContext();
  const { selectedProduct, orgId, clientId } = useProductDataOutsideRoutes();
  const location = useLocation();

  const isSubRouteActive = !!submenu?.some(({ route, routeParams: subRouteParams }) =>
    route &&
    matchPath(location.pathname, {
      path: buildRoute(route, { orgId, selectedProduct, clientId, ...subRouteParams }),
      exact: false,
    }),
  );
  const stepsStatus = useGetStartedVodStatus();
  const { delegatedFrame } = useDelegatedFrameContext();

  const handleNavigationLinkClick = (e, route) => {
    if (disabled) return;

    setSelectedNavigationItemTitle(null);

    const url = buildRoute(route, {
      orgId,
      selectedProduct,
      clientId,
      ...routeParams,
    });
    trackingService.track({
      eventType: 'sidebar-navigation',
      destination: title,
      path: url,
    });

    if (delegatedFrame?.supportsRoute(route)) {
      delegatedFrame.changeRoute(url);
      e.preventDefault();
      return false;
    }
  };

  const handleButtonClick = () => {
    if (disabled) return;

    setSelectedNavigationItemTitle((prevTitle) => {
      if (prevTitle === title) {
        trackingService.track({
          eventType: 'sidebar-navigation:collapse-submenu',
          destination: title,
        });
        return null;
      }
      trackingService.track({
        eventType: 'sidebar-navigation:expand-submenu',
        destination: title,
      });
      return title;
    });
  };

  const isCustomIcon = icon === 'progress';
  const stepsValues = Object.entries(stepsStatus)
    .filter(([key]) => STEPS.includes(key))
    .map(([, value]) => value);

  const progress = stepsValues.reduce((acc, step) => (step ? acc + 1 : acc), 0) / stepsValues.length;
  const progressProps = {
    progress,
    size: 'medium',
    checkmarkColor: COLORS.white,
    circleColor: COLORS.white,
  };

  return (
    <MenuList key={title} {...props}>
      {route && !url && (
        <NavLink
          title={title}
          className={cx(navigationBaseStyles, navigationLinkStyles, disabled && disabledNavigationLinkStyles)}
          to={buildRoute(route, { orgId, selectedProduct, clientId, ...routeParams })}
          onClick={(e) => handleNavigationLinkClick(e, route)}
          data-test-id={dataTestId}
          exact={exact}
        >
          {numberIndicators?.map((props, index) => <StyledNumberIndicator key={index} {...props} />)}
          {flair && <StyledFlair {...flair} />}
          {isCustomIcon && icon === 'progress' && (
            <CustomIconWrapper padding={8}>
              <Progress {...progressProps} />
            </CustomIconWrapper>
          )}
          {icon && !isCustomIcon && (
            <IconWrapper>
              <Icon name={icon} className="menu-icon" />
            </IconWrapper>
          )}
          <span className={titleStyles}>{title}</span>
        </NavLink>
      )}
      {url && (
        <a
          title={title}
          className={cx(navigationBaseStyles, navigationLinkStyles, disabled && disabledNavigationLinkStyles)}
          href={url}
          onClick={handleNavigationLinkClick}
          data-test-id={dataTestId}
        >
          {numberIndicators?.map((props, index) => <StyledNumberIndicator key={index} {...props} />)}
          {flair && <StyledFlair {...flair} />}
          {isCustomIcon && icon === 'progress' && (
            <CustomIconWrapper padding={8}>
              <Progress {...progressProps} />
            </CustomIconWrapper>
          )}
          {icon && !isCustomIcon && (
            <IconWrapper>
              <Icon name={icon} className="menu-icon" />
            </IconWrapper>
          )}
          <span className={titleStyles}>{title}</span>
        </a>
      )}
      {submenu && (
        <ButtonWrapper>
          <button
            title={title}
            onClick={handleButtonClick}
            className={cx(
              navigationBaseStyles,
              navigationButtonStyles,
              (title === selectedNavigationItemTitle || isSubRouteActive) && 'active',
              disabled && disabledNavigationLinkStyles,
            )}
          >
            {numberIndicators?.map((props, index) => <StyledNumberIndicator key={index} {...props} />)}
            {flair && <StyledFlair {...flair} />}
            {isCustomIcon && icon === 'progress' && (
              <CustomIconWrapper padding={8}>
                <Progress {...progressProps} />
              </CustomIconWrapper>
            )}
            {icon && !isCustomIcon && (
              <IconWrapper>
                <Icon name={icon} className="menu-icon" />
              </IconWrapper>
            )}
            <span className={titleStyles}>{title}</span>
          </button>
          <SubMenu
            submenu={submenu}
            disabled={disabled}
            isShown={title === selectedNavigationItemTitle}
            align={align}
          />
        </ButtonWrapper>
      )}
    </MenuList>
  );
};

export default ProductsNavigationListItem;
